// Canonical color aliases — MUST stay in sync with backend routes_catalog.py
// (`CANONICAL_COLOR`). Each canonical key maps to the raw DB values it should
// match. Used to pick the correct variant image/label when the user has
// filtered by a specific color on Shop / CategorySilo / BrandSilo pages.
const CANONICAL = {
  Black:     ["black"],
  White:     ["white", "off white", "off-white"],
  Ivory:     ["ivory", "cream"],
  Champagne: ["champagne", "champagne gold", "champagne pink"],
  Blush:     ["blush", "dusty pink", "dusty rose", "rose"],
  Pink:      ["pink", "hot pink", "bright pink", "light pink", "neon pink", "fuchsia", "babypink", "baby pink"],
  Red:       ["red", "dark red", "wine", "cherry", "deep red"],
  Burgundy:  ["burgundy", "merlot", "bordeaux"],
  Coral:     ["coral", "peach"],
  Yellow:    ["yellow", "bright yellow"],
  Green:     ["green", "bright green", "kelly green", "lime green", "lime", "olive"],
  Emerald:   ["emerald", "dark emerald", "hunter green", "bright emerald", "jade"],
  Celadon:   ["celadon", "sage", "sage green", "dark sage", "soft mint"],
  Teal:      ["teal", "turquoise", "bright teal"],
  Blue:      ["blue", "light blue", "bright blue", "cornflower", "sky blue"],
  Navy:      ["navy", "dark navy", "midnight"],
  Royal:     ["royal", "royal blue", "bright royal", "cobalt"],
  Purple:    ["purple", "bright purple", "magenta", "plum", "eggplant"],
  Lavender:  ["lavender", "lilac", "periwinkle", "light purple", "mauve"],
  Silver:    ["silver", "platinum"],
  Gray:      ["gray", "grey", "charcoal"],
  Gold:      ["gold", "metallic gold", "antique gold", "golden", "light gold", "dark gold"],
  "Rose Gold": ["rose gold"],
  TAN:       ["tan", "beige", "camel", "nude"],
  Brown:     ["brown"],
  Chocolate: ["chocolate", "espresso", "mocha"],
  Cognac:    ["cognac", "caramel"],
};

const norm = (s) => String(s || "").trim().toLowerCase();

// Hex swatches for solid color chips. Keyed by canonical DB values (many
// raw color spellings included so the PDP swatch resolver "just works"
// without an extra canonicalize step).
const COLOR_HEX = {
  black: "#0A0A0A", white: "#FAFAFA", "off white": "#FAFAFA",
  ivory: "#F7F1E7", cream: "#F7F1E7",
  champagne: "#F7E7CE", "champagne gold": "#EAD9A8", "champagne pink": "#EFCFC9",
  blush: "#F3D1D3", "dusty pink": "#E8B0B2", "dusty rose": "#D89A9C", rose: "#D89A9C",
  pink: "#FFB6C1", "hot pink": "#FF3E96", "bright pink": "#FF3E96", "light pink": "#FFD1DC",
  "neon pink": "#FF6EC7", fuchsia: "#C71585", magenta: "#B60063",
  babypink: "#FBC3D3", "baby pink": "#FBC3D3", "candy pink": "#F6A3C3",
  red: "#C8102E", "dark red": "#8B0F1E", "deep red": "#8B0F1E", wine: "#5C1A22", cherry: "#B31329",
  burgundy: "#5C1A22", merlot: "#4C1520", bordeaux: "#4C1520",
  coral: "#FF7F50", peach: "#FFB088",
  yellow: "#F5CB3A", "bright yellow": "#FFD400",
  green: "#3F8F3A", "bright green": "#3AA831", "kelly green": "#4CBB17",
  "lime green": "#B8E434", lime: "#B8E434", olive: "#708238",
  emerald: "#087E5B", "dark emerald": "#065C43", "hunter green": "#355E3B",
  "bright emerald": "#0AA274", jade: "#008872",
  celadon: "#ACE1AF", sage: "#B4C4A5", "sage green": "#B4C4A5", "dark sage": "#7A8C6B",
  "soft mint": "#C6E4CB",
  teal: "#0D8A8A", turquoise: "#40E0D0", "bright teal": "#00CED1",
  blue: "#3A6BD6", "light blue": "#ADD8E6", "bright blue": "#0057D9", "sky blue": "#87CEEB",
  cornflower: "#6495ED",
  navy: "#0A2452", "dark navy": "#06183A", midnight: "#04122D",
  royal: "#1E3A8A", "royal blue": "#1E3A8A", "bright royal": "#1E3A8A", cobalt: "#0047AB",
  purple: "#7A3AA8", "bright purple": "#8A2BE2", plum: "#8E4585", eggplant: "#614051",
  lavender: "#C6B5E0", lilac: "#C8A2C8", periwinkle: "#B6C0E1",
  "light purple": "#CBB0DF", mauve: "#B784A7",
  silver: "#C5C6C7", platinum: "#E5E4E2",
  gray: "#808080", grey: "#808080", charcoal: "#36454F",
  gold: "#D4A94A", "metallic gold": "#D4A94A", "antique gold": "#B8862F",
  golden: "#D4A94A", "light gold": "#E6C670", "dark gold": "#8F6A16",
  "rose gold": "#B76E79",
  tan: "#D2B48C", beige: "#D8C6A0", camel: "#C19A6B", nude: "#E3BC9A",
  brown: "#8B4513",
  chocolate: "#5A3A22", espresso: "#3D2B1F", mocha: "#8E6B4C",
  cognac: "#9A463D", caramel: "#AF6E4D",
  peacock: "#005F73",
  "neon orange": "#FF7538", "neon green": "#39FF14",
};

/** Case-insensitive hex lookup for a raw DB color value. Returns null if unknown. */
export function colorHex(raw) {
  if (!raw) return null;
  const key = norm(raw);
  if (COLOR_HEX[key]) return COLOR_HEX[key];
  // Compound "Black/Gold" — return null so caller can render a duo-swatch itself.
  return null;
}

/** Parse a compound color ("Black/Gold", "Royal/Turquoise", "Ivory/Gold/Silver"). */
export function splitCompoundColor(raw) {
  if (!raw) return [];
  return String(raw).split(/[\/&+]/).map((s) => s.trim()).filter(Boolean);
}

/** True iff `dbColor` (raw DB value) canonicalizes to `filter` (a canonical key). */
export function matchesCanonicalColor(dbColor, filter) {
  if (!dbColor || !filter) return false;
  const patterns = CANONICAL[filter] || [norm(filter)];
  return patterns.includes(norm(dbColor));
}

/**
 * Given a product and the color the user is filtering by, return the variant
 * whose `color` matches the filter (via canonical aliases). Falls back to the
 * primary image/color when nothing matches (or no filter is active).
 * Returns `{ image, color }` — safe to render directly.
 */
export function pickVariantForColor(product, filter) {
  const primary = { image: product?.images?.[0], color: product?.color };
  if (!filter) return primary;
  // 1) Primary product color already matches — no need to swap
  if (matchesCanonicalColor(product?.color, filter)) return primary;
  // 2) Find the first variant whose color matches
  const v = (product?.variants || []).find((v) => matchesCanonicalColor(v?.color, filter));
  if (v && v.image) return { image: v.image, color: v.color };
  return primary;
}
